import { ArrowLeft, Heart, Link as LinkIcon } from 'lucide-react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import DecisionPoint from '~/components/spec/DecisionPoint';
import DepthExplorer from '~/components/spec/DepthExplorer';
import OptionSelector from '~/components/spec/OptionSelector';
import TradeoffPanel from '~/components/spec/TradeoffPanel';
import { getFeatureBySlug, getSpecsUsingFeature } from '~/lib/db/queries/specs';
import { CATEGORY_LABELS, COMPLEXITY_COLORS } from '~/lib/utils';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => [
  {
    title: loaderData
      ? `${loaderData.feature.name} Feature — Specly`
      : 'Feature — Specly',
  },
];

export async function loader({ params }: LoaderFunctionArgs) {
  const feature = await getFeatureBySlug(params.slug!);
  if (!feature) throw new Response('Not Found', { status: 404 });
  const usedBySpecs = await getSpecsUsingFeature(feature.id);
  return { feature, usedBySpecs };
}

export default function FeatureDetail() {
  const { feature, usedBySpecs } = useLoaderData<typeof loader>();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        All specs
      </Link>

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-gray-500">
            {CATEGORY_LABELS[feature.category] ?? feature.category}
          </span>
          <span
            className={`text-xs font-medium border rounded-full px-2 py-0.5 ${COMPLEXITY_COLORS[feature.complexity]}`}
          >
            {feature.complexity} complexity
          </span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{feature.name}</h1>
        {usedBySpecs.length > 0 && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-xs font-semibold">
              Used in {usedBySpecs.length} spec
              {usedBySpecs.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <p className="mt-2 text-gray-600">{feature.description}</p>
      </div>

      {feature.definition.optionGroups.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Options</h2>
          <div className="space-y-4">
            {feature.definition.optionGroups.map((group) => (
              <div
                key={group.id}
                className="bg-white border border-gray-200 rounded-xl p-4"
              >
                <OptionSelector
                  group={group}
                  selected={group.options
                    .filter((o) => o.presets.includes('standard'))
                    .map((o) => o.id)}
                  readOnly
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {feature.definition.decisionPoints.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Decision Points
          </h2>
          <div className="space-y-3">
            {feature.definition.decisionPoints.map((dp, i) => (
              <DecisionPoint key={i} point={dp} />
            ))}
          </div>
        </section>
      )}

      {feature.definition.tradeoffs.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Tradeoffs
          </h2>
          <TradeoffPanel tradeoffs={feature.definition.tradeoffs} />
        </section>
      )}

      {feature.definition.depthLayers &&
        feature.definition.depthLayers.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Go Deeper
            </h2>
            <DepthExplorer layers={feature.definition.depthLayers} />
          </section>
        )}

      {feature.definition.dependencies.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Dependencies
          </h2>
          <div className="flex flex-wrap gap-2">
            {feature.definition.dependencies.map((dep) => (
              <Link
                key={dep}
                to={`/features/${dep}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
              >
                <LinkIcon className="h-3.5 w-3.5" />
                {dep}
              </Link>
            ))}
          </div>
        </section>
      )}

      {feature.definition.implementationExamples &&
        feature.definition.implementationExamples.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Implementation Examples
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {feature.definition.implementationExamples.map((example) => (
                <a
                  key={example.url}
                  href={example.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 group-hover:text-indigo-700 transition-colors">
                      {example.name}
                    </span>
                    <LinkIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0" />
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {example.description}
                  </p>
                </a>
              ))}
            </div>
          </section>
        )}

      {usedBySpecs.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Used by specs
          </h2>
          <div className="flex flex-wrap gap-2">
            {[...usedBySpecs]
              .sort((a, b) => (b.starCount ?? 0) - (a.starCount ?? 0))
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/specs/${s.slug}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-sm hover:bg-indigo-100 transition-colors"
                >
                  <span>{s.name}</span>
                  {s.starCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-indigo-500">
                      <Heart className="size-3" />
                      {s.starCount}
                    </span>
                  )}
                </Link>
              ))}
          </div>
        </section>
      )}
    </div>
  );
}
